import type { SitemapPathEntry } from '@/lib/seo/sitemap';
import type { MetadataRoute } from 'next';

/**
 * Marketing routes centralize everything we need to know about "brochure" URLs
 * (public access, Framer proxying, sitemap metadata, etc.) so middleware,
 * rewrites, sitemap generation, and the Framer edge handler all stay in sync.
 */
type MarketingRouteHandler = 'framer' | 'app';

type SitemapConfig = {
  changeFrequency: MetadataRoute.Sitemap[number]['changeFrequency'];
  priority: MetadataRoute.Sitemap[number]['priority'];
};

export type MarketingRoute = {
  path: `/${string}` | '/';
  handler: MarketingRouteHandler;
  includeChildren?: boolean;
  public?: boolean;
  sitemap?: SitemapConfig;
};

type NormalizedMarketingRoute = MarketingRoute & {
  includeChildren: boolean;
  public: boolean;
};

/**
 * Single source of truth for all marketing pages. Most entries are Framer-hosted,
 * but we include app-rendered ones (download, terms, 404, etc.) so middleware and
 * the sitemap treat them the same way.
 */
const RAW_MARKETING_ROUTES: readonly MarketingRoute[] = [
  {
    path: '/',
    handler: 'framer',
    sitemap: {
      changeFrequency: 'daily',
      priority: 1.0,
    },
  },
  {
    path: '/customers',
    handler: 'framer',
    includeChildren: true,
    sitemap: {
      changeFrequency: 'weekly',
      priority: 0.8,
    },
  },
  {
    path: '/blog',
    handler: 'framer',
    includeChildren: true,
    sitemap: {
      changeFrequency: 'daily',
      priority: 0.8,
    },
  },
  {
    path: '/examples',
    handler: 'framer',
    includeChildren: true,
    sitemap: {
      changeFrequency: 'weekly',
      priority: 0.8,
    },
  },
  {
    path: '/pricing',
    handler: 'app',
  },
  {
    path: '/pricing-plans',
    handler: 'framer',
    sitemap: {
      changeFrequency: 'weekly',
      priority: 0.8,
    },
  },
  {
    path: '/codebase-scans',
    handler: 'framer',
    sitemap: {
      changeFrequency: 'weekly',
      priority: 0.7,
    },
  },
  {
    path: '/learnings',
    handler: 'framer',
    sitemap: {
      changeFrequency: 'weekly',
      priority: 0.7,
    },
  },
  {
    path: '/enterprise',
    handler: 'framer',
    sitemap: {
      changeFrequency: 'weekly',
      priority: 0.8,
    },
  },
  {
    path: '/state-of-ai-coding-2026',
    handler: 'framer',
    includeChildren: false,
    sitemap: {
      changeFrequency: 'monthly',
      priority: 0.7,
    },
  },
  // Articles from The Prompting Company, proxied by src/app/info (see src/constants/info-content.ts).
  {
    path: '/info',
    handler: 'app',
    includeChildren: true,
    sitemap: {
      changeFrequency: 'daily',
      priority: 0.7,
    },
  },
  {
    path: '/download',
    handler: 'app',
    sitemap: {
      changeFrequency: 'weekly',
      priority: 0.7,
    },
  },
  // Public agent catalog. Public paths are prefix-matched, so child pages are public too;
  // sitemap.ts lists them from the catalog data.
  {
    path: '/standards',
    handler: 'app',
    sitemap: {
      changeFrequency: 'weekly',
      priority: 0.8,
    },
  },
  {
    path: '/agents',
    handler: 'app',
    sitemap: {
      changeFrequency: 'weekly',
      priority: 0.7,
    },
  },
  // The AI code review guide.
  {
    path: '/ai-code-review',
    handler: 'app',
    sitemap: {
      changeFrequency: 'monthly',
      priority: 0.9,
    },
  },
  // The ranked list of AI code review tools.
  {
    path: '/best-ai-code-review-tools',
    handler: 'app',
    sitemap: {
      changeFrequency: 'monthly',
      priority: 0.9,
    },
  },
  // cubic vs other AI code reviewers; sitemap.ts lists each comparison page.
  {
    path: '/vs',
    handler: 'app',
    sitemap: {
      changeFrequency: 'monthly',
      priority: 0.7,
    },
  },
  {
    path: '/terms',
    handler: 'app',
  },
  {
    path: '/privacy',
    handler: 'app',
  },
  {
    path: '/404',
    handler: 'app',
  },
  {
    path: '/demo',
    handler: 'app',
  },
  {
    path: '/paul',
    handler: 'app',
  },
] as const;

export const MARKETING_ROUTES: readonly NormalizedMarketingRoute[] = RAW_MARKETING_ROUTES.map(
  (route) => ({
    ...route,
    includeChildren: route.includeChildren ?? false,
    public: route.public ?? true,
  }),
);

const FRAMER_ROUTES = MARKETING_ROUTES.filter((route) => route.handler === 'framer');

/**
 * Returns all marketing paths that should be treated as public routes by the middleware.
 * This keeps the auth layer aligned with whatever is listed in `RAW_MARKETING_ROUTES`.
 */
export function getPublicMarketingPaths(): string[] {
  return MARKETING_ROUTES.filter((route) => route.public).map((route) => route.path);
}

/**
 * Checks whether a given pathname is configured to be proxied from Framer instead of
 * rendered by our Next.js app. Used by the `/framer` route to gate requests.
 */
export function isFramerMarketingPath(pathname: string): boolean {
  const normalizedPath =
    pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  return FRAMER_ROUTES.some((route) => {
    if (route.path === '/') {
      return normalizedPath === '/';
    }

    if (route.includeChildren) {
      return normalizedPath === route.path || normalizedPath.startsWith(`${route.path}/`);
    }

    return normalizedPath === route.path;
  });
}

/**
 * Generates Next.js rewrite rules for Framer-hosted marketing paths so we can
 * route them through our `/framer` handler while keeping canonical URLs at the edge.
 */
export function getFramerRewriteRules() {
  return [
    ...FRAMER_ROUTES.filter((route) => route.path === '/').map(() => ({
      source: '/',
      destination: '/framer',
    })),
    ...FRAMER_ROUTES.filter((route) => route.path !== '/').map((route) => {
      if (route.includeChildren) {
        return {
          source: `${route.path}/:path*`,
          destination: `/framer${route.path}/:path*`,
        };
      }

      return {
        source: route.path,
        destination: `/framer${route.path}`,
      };
    }),
  ];
}

/**
 * Builds sitemap entries (path, priority, frequency) for every marketing route
 * that opts into the sitemap metadata within `RAW_MARKETING_ROUTES`.
 */
export function getMarketingSitemapEntries(): SitemapPathEntry[] {
  return MARKETING_ROUTES.filter((route) => route.sitemap).map((route) => ({
    path: route.path,
    changeFrequency: route.sitemap?.changeFrequency ?? 'weekly',
    priority: route.sitemap?.priority ?? 0.6,
  }));
}
